import { readNumber } from '@/domains/survey';
import type {
  InternalFieldType,
  InternalPayload,
  IInternalValueOption
} from './api/internal-field-contracts';
import { formatNumber } from './answer-text';
import { formatDay } from './components/days';
import { formatMoney } from './components/sec-adv-values';

/**
 * The two-way translation between survey-api's internal-field payload and what a control holds —
 * the admin twin of the runtime's `answer-values.ts`, and the one reader of internal-field type →
 * payload (the advisor record page's roadmap, K2; the F1 kit plan's R5).
 *
 * **The mapping is per field type, not per control**, for `answer-values.ts`' reason: a write is
 * judged against the field's type, and a payload whose tag is not that type is refused before
 * anything else is read. Controls hold text (an `<input>` has no other state), a tick, or option
 * codes in the order they were ticked.
 *
 * **Nothing here judges a value.** No length, no bound, no decimals and no mirror of
 * `FieldConstraintDefaults` — survey-api does not even merge an imported field's constraint on the
 * wire, and its 400 is the judge (CLAUDE.md's rules of the code). What this module does decide is
 * the runtime's narrow question, "can a payload of this type carry the text at all": `abc` in an
 * `int` is **malformed**, held by the field rather than sent and rather than read as a clear
 * (`isMalformedInternal`). An emptied control is a clear, and a clear is a `DELETE`, never a
 * payload: survey-api refuses a `null` `v`.
 *
 * Numbers are read by the respondent form's own rule (`readNumber`), so `1,500` and `12.5%` mean
 * here what they mean there. Rejected: a second number reader, which would make `1,500` an amount
 * in one place and malformed in the other.
 */

/** The types this app has a control and a reading for, in survey-api's own order
 *  (`InternalFieldTypes.All`). Exported because an author picks from it (SR-226): a second
 *  transcription would be a second place to add the next type to. */
export const KNOWN_INTERNAL_TYPES = [
  'text',
  'long_text',
  'rich_text',
  'int',
  'decimal',
  'money',
  'percent',
  'date',
  'bool',
  'select',
  'multi_select'
] satisfies InternalFieldType[];

const KNOWN_TYPES = new Set<string>(KNOWN_INTERNAL_TYPES);

/** Whether a type survey-api sends is one this app reads — a newer one is drawn as written. */
export function isInternalFieldType(type: string): type is InternalFieldType {
  return KNOWN_TYPES.has(type);
}

/** What a control holds: text, a tick (`bool`), or option codes (`multi_select`). */
export type InternalUiValue = string | boolean | string[];

/** The control's starting value for an absent payload, so `undefined` never reaches an input.
 *  `bool` starts unticked — and unticked is the value `false`, never "no value" (U4). */
function emptyUi(type: InternalFieldType): InternalUiValue {
  if (type === 'bool') return false;
  if (type === 'multi_select') return [];
  return '';
}

/** Wire → control. */
export function decodeInternalValue(
  payload: Nullable<InternalPayload>,
  type: InternalFieldType
): InternalUiValue {
  if (!payload) return emptyUi(type);
  switch (payload.t) {
    case 'bool':
      return payload.v;
    case 'multi_select':
      return [...payload.v];
    case 'int':
    case 'decimal':
    case 'money':
    case 'percent':
      return String(payload.v);
    default:
      return payload.v;
  }
}

/**
 * Control → wire. `null` is an empty control — a clear — or text no payload of this type can
 * carry, which `isMalformedInternal` tells apart; the field holds the second and never sends it.
 */
export function encodeInternalValue(
  type: InternalFieldType,
  ui: InternalUiValue
): Nullable<InternalPayload> {
  if (type === 'bool') return { t: 'bool', v: ui === true };
  if (type === 'multi_select') {
    const codes = Array.isArray(ui) ? ui.filter(Boolean) : [];
    return codes.length > 0 ? { t: 'multi_select', v: codes } : null;
  }
  if (typeof ui !== 'string') return null;

  if (type === 'rich_text') return richTextIsEmpty(ui) ? null : { t: 'rich_text', v: ui };

  const text = ui.trim();
  if (text === '') return null;

  switch (type) {
    case 'text':
    case 'long_text':
    case 'select':
      return { t: type, v: text };
    case 'date':
      // The payload is yyyy-MM-dd or nothing. A date input hands over `''` for a half-typed date,
      // which `AdminInlineField` tells from an emptied one by the element's `validity.badInput`.
      return /^\d{4}-\d{2}-\d{2}$/.test(text) ? { t: 'date', v: text } : null;
    case 'int': {
      const parsed = readNumber(text);
      return Number.isInteger(parsed) ? { t: 'int', v: parsed } : null;
    }
    case 'decimal':
    case 'money':
    case 'percent': {
      const parsed = readNumber(text);
      return Number.isFinite(parsed) ? { t: type, v: parsed } : null;
    }
  }
}

/**
 * Whether rich text says nothing — no text and no image. The editor hands back `''` for an empty
 * document, and a stored value can be markup around nothing (`<p></p>`, a table of empty cells),
 * which is a clear too: storing it would make a field read as filled and show nothing.
 */
export function richTextIsEmpty(html: string): boolean {
  if (html.trim() === '') return true;
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  return (body.textContent ?? '').trim() === '' && body.querySelector('img') === null;
}

/** Whether the control holds something no payload of this type can carry — `abc` in an `int`,
 *  `12.5.6` in a percent. Not validation: survey-api judges every value that can be sent. */
export function isMalformedInternal(type: InternalFieldType, ui: InternalUiValue): boolean {
  if (typeof ui !== 'string' || type === 'rich_text') return false;
  return ui.trim() !== '' && encodeInternalValue(type, ui) === null;
}

/** What to say for a held value — the shape the field wants, in the respondent form's words
 *  (`malformedHint`), never the tag on the wire. */
export function internalMalformedHint(type: InternalFieldType): string {
  switch (type) {
    case 'int':
      return 'Enter a whole number — digits only.';
    case 'decimal':
    case 'percent':
      return 'Enter a number, for example 12.5.';
    case 'money':
      return 'Enter an amount, for example 1,500,000.';
    case 'date':
      return 'Enter a full date.';
    default:
      return 'This entry cannot be saved as it is.';
  }
}

/**
 * Whether two payloads say the same thing — so a value saved unchanged sends nothing (the
 * runtime's no-op rule). A `multi_select`'s order counts: survey-api keeps the order the options
 * were chosen in.
 */
export function sameInternalValue(
  a: Nullable<InternalPayload>,
  b: Nullable<InternalPayload>
): boolean {
  if (a === null || b === null) return a === b;
  if (a.t !== b.t) return false;
  if (a.t === 'multi_select') {
    const other = b.v as string[];
    return a.v.length === other.length && a.v.every((code, index) => code === other[index]);
  }
  return a.v === b.v;
}

/**
 * A stored value as it reads — every type but `rich_text`, which is markup and drawn by
 * `RichText`. A code is read by the label the value carries for it (a retired option's included)
 * and falls back to itself; a number is grouped and never rounded, an amount by the app's money
 * rendering, a percent as `12.5%` (survey-api's 0–100 scale), a date as the day it names.
 */
export function internalValueText(
  payload: InternalPayload,
  options: readonly IInternalValueOption[] = []
): string {
  const label = (code: string) => options.find((option) => option.code === code)?.label ?? code;
  switch (payload.t) {
    case 'bool':
      return payload.v ? 'Checked' : 'Not checked';
    case 'int':
    case 'decimal':
      return formatNumber(payload.v);
    case 'money':
      return formatMoney(payload.v);
    case 'percent':
      return `${formatNumber(payload.v)}%`;
    case 'date':
      return formatDay(payload.v);
    case 'select':
      return label(payload.v);
    case 'multi_select':
      return payload.v.map(label).join(', ');
    default:
      return payload.v;
  }
}

/**
 * A value as a work item's row carries it (`internal`, SR-222's F9) — survey-api's flat text, the
 * stored `ValueText` or else the number in invariant culture — read back as the payload of the
 * field's type, so the queue draws it as the hub draws the same value (`InternalFieldDisplay`): a
 * select's code by its label, a number grouped, a date as its day, `true` as a tick. Null where the
 * text is not one of that type — a multi-select (survey-api leaves them out), a type this app does
 * not know, or text that does not parse — and the caller then draws it as spelled.
 *
 * Numbers are read with `Number`, not `readNumber`: invariant culture writes no grouping, and a
 * value that came back otherwise is not one this function should guess at.
 */
export function flatInternalPayload(text: string, type: string): Nullable<InternalPayload> {
  switch (type) {
    case 'text':
    case 'long_text':
    case 'rich_text':
    case 'select':
      return { t: type, v: text };
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(text) ? { t: 'date', v: text } : null;
    case 'bool':
      return text === 'true' || text === 'false' ? { t: 'bool', v: text === 'true' } : null;
    case 'int':
    case 'decimal':
    case 'money':
    case 'percent': {
      const parsed = text.trim() === '' ? Number.NaN : Number(text);
      if (!Number.isFinite(parsed)) return null;
      if (type === 'int') return Number.isInteger(parsed) ? { t: 'int', v: parsed } : null;
      return { t: type, v: parsed };
    }
    default:
      return null;
  }
}
